import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import type { EventMap } from '@zep/logger';
import { trackEvent } from '@zep/logger/tracking';

import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { useDeleteQuizSet } from '@/api/gen/quiz/quiz.gen';
import { showShareModal } from '@/components';
import { useOpenWindow } from '@/hooks';
import { useUser } from '@/lib/auth';
import {
  buildFolderPlayHref,
  invalidateFolderRelated,
  reportFolderError,
  toTrackingFailReason,
  trackSaveEntryAuthRequired,
  useShowSaveToFolderModal,
} from '@/shared/features/quiz-folder';
import { useCreateAssignmentDialog } from '@/shared/googleClassroom';
import { usePlayUrl } from '@/shared/hooks';
import { useLtiSelectAction } from '@/shared/lti/hooks/useLtiSelectAction';
import { useQuizListItemProvider } from '@/shared/quizList/hooks/QuizListItemContext';
import { type QuizCardMenuItem } from '@/shared/quizList/hooks/useGetQuizListItemButtons';
import { useQuizCardEventTrack } from '@/shared/quizList/hooks/useQuizCardEventTrack';
import { confirmDialog } from '@/ui_v3';

/** 폴더 안 목록에서만 의미가 있는 이벤트. `folder_id` 는 헬퍼가 채운다. */
type InFolderEvent =
  | 'quiz_folder_item_delete_save'
  | 'quiz_folder_item_delete_cancel'
  | 'quiz_folder_play_entry_click';

export const useQuizListItemAction = (quizSet: SearchQuizsetRes) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { mutateAsync } = useDeleteQuizSet();
  const { t } = useTranslation(['common']);
  const { trackButtons } = useQuizCardEventTrack();
  /* 이 목록이 어느 폴더 안에서 그려지는지 — 재생 맥락. 폴더 밖 목록에서는 비어 있다. */
  const { folderPlayOrigin, eventLocation } = useQuizListItemProvider();
  // 이 퀴즈가 지금 담긴 폴더. 폴더 상세 응답은 담긴 퀴즈의 `folderId` 를 비워 보낸다 — 그 폴더 목록에 있다는
  // 것이 곧 소속이므로 목록의 폴더로 채운다.
  const currentFolderId = quizSet.folderId ?? folderPlayOrigin;
  // 저장·이동을 시작한 자리. 폴더 상세의 행인지 그 밖의 퀴즈 목록인지로 나눈다.
  const savePlace =
    eventLocation === 'folder' ? 'folder_detail_card' : 'quiz_list_menu';
  const createAssignmentDialog = useCreateAssignmentDialog();
  const playUrl = usePlayUrl(quizSet.entryMapHashId);
  const { selectQuiz: selectLtiQuiz } = useLtiSelectAction();
  const showSaveToFolderModal = useShowSaveToFolderModal();
  const isQuizOwner = !!user && user.quizUserId === quizSet.authorId;

  const openPage = useOpenWindow();

  // 폴더 밖 목록(`folderPlayOrigin` 없음)에서는 조용히 건너뛴다.
  const trackInFolder = <K extends InFolderEvent>(
    name: K,
    props: Omit<EventMap[K], 'folder_id'>,
  ) => {
    if (!folderPlayOrigin) return;
    trackEvent(name, { ...props, folder_id: folderPlayOrigin } as EventMap[K]);
  };

  const handleShareModal = () => {
    return showShareModal({
      ...quizSet,
      shareUrl: playUrl,
      contentType: 'QUIZSET',
      contentId: quizSet.id,
      contentOwnerId: quizSet.authorId,
      spaceHashId: quizSet.spaceHashId,
      trackLocation: 'public',
    });
  };

  const openLmsPage = () => {
    openPage(`/lms/${quizSet.id}/learning-report`);
  };
  const deleteAction = () =>
    mutateAsync({
      data: {
        id: quizSet.id,
      },
    })
      .then(() => {
        trackInFolder('quiz_folder_item_delete_save', {
          result: 'success',
          place: 'folder_detail_menu',
          quiz_set_id: quizSet.id,
        });
        // 폴더 상세는 공개 폴더면 엣지에 캐시된다. 새로고침하면 캐시된 옛 목록(방금 담은 퀴즈가 없고
        // 지운 퀴즈가 남은)을 받으므로, 폴더 안에서는 조회만 다시 받는다.
        if (folderPlayOrigin) {
          invalidateFolderRelated(queryClient, folderPlayOrigin);
          return;
        }
        router.reload();
      })
      .catch((error: unknown) => {
        trackInFolder('quiz_folder_item_delete_save', {
          result: 'fail',
          reason: toTrackingFailReason(error),
          place: 'folder_detail_menu',
          quiz_set_id: quizSet.id,
        });
        if (folderPlayOrigin) {
          reportFolderError(error, {
            action: 'delete_item',
            folderId: folderPlayOrigin,
            quizSetId: quizSet.id,
          });
        }
        // 기록만 더한다. 실패는 전처럼 호출부로 흘려보낸다.
        throw error;
      });

  const onClickEvent = (actionType: QuizCardMenuItem['onClickEventName']) => {
    trackButtons(actionType);
    switch (actionType) {
      case 'lti_select': {
        void selectLtiQuiz({
          quizSetId: quizSet.id,
          entryMapHashId: quizSet.entryMapHashId,
          quizTitle: quizSet.title,
        });
        break;
      }
      case 'google_assignment': {
        createAssignmentDialog.open({
          quizSet,
        });
        break;
      }
      case 'share':
        handleShareModal();
        break;
      case 'copy':
        // 내 퀴즈 복사도 저장 위치를 골라 즉시 복사한다. 복사는 원본 위치와 무관하므로 현재 폴더를 넘기지 않는다.
        void showSaveToFolderModal({
          id: quizSet.id,
          intent: 'copy',
          place: savePlace,
          isQuizOwner,
        });
        break;
      case 'save_to_my_quiz':
        // 비로그인이면 저장할 곳이 없다. 로그인으로 보내고 지금 화면으로 돌려보낸다.
        if (!user) {
          trackSaveEntryAuthRequired(savePlace, quizSet.id);
          void router.push({
            pathname: '/login',
            query: { callbackUrl: router.asPath },
          });
          break;
        }
        // ⚠️ 소유권으로 intent 를 정하는 유일한 자리다. 이 행은 남의 폴더에도 뜨는데, 그 폴더에 내 퀴즈가
        // 담겨 있으면 복사가 아니라 이동이어야 한다. 판정 기준은 퀴즈의 작성자다.
        void showSaveToFolderModal({
          id: quizSet.id,
          intent: isQuizOwner ? 'move' : 'copy',
          currentFolderId,
          place: savePlace,
          isQuizOwner,
        });
        break;
      case 'move_to_folder':
        // 지금 소속 폴더는 대상에서 빼고 `기본 내 퀴즈` 선택지를 연다.
        void showSaveToFolderModal({
          id: quizSet.id,
          intent: 'move',
          currentFolderId,
          place: savePlace,
          isQuizOwner,
        });
        break;
      case 'report':
        openLmsPage();
        break;
      case 'enter': {
        trackInFolder('quiz_folder_play_entry_click', {
          place: 'folder_detail_row',
          entry_quiz_set_id: quizSet.id,
        });
        // 폴더 안에서 시작한 재생이면 폴더를 이어 간다. `folderPlayOrigin` 이 없으면 단일 재생이다.
        const playHref = folderPlayOrigin
          ? buildFolderPlayHref({
              folderId: folderPlayOrigin,
              entryMapHashId: quizSet.entryMapHashId,
            })
          : `/play/${quizSet.entryMapHashId}`;
        void router.push(playHref);
        break;
      }
      case 'update':
        void router.push(`/update/${quizSet.id}`);
        break;
      case 'delete':
        void confirmDialog
          .openAsync({
            header: {
              title: t('common:quizListItem.error.delete'),
            },
            confirmIntent: 'destructive',
            cancelIntent: 'neutral',
          })
          .then(result => {
            if (result) {
              void deleteAction();
              return;
            }
            trackInFolder('quiz_folder_item_delete_cancel', {
              place: 'folder_detail_menu',
              quiz_set_id: quizSet.id,
            });
          });

        break;
    }
  };

  return {
    onClickEvent,
  };
};
